import React, { Component } from 'react';
import { arrayOf, bool, func, node, number, object, string } from 'prop-types';

import { injectIntl, intlShape } from '../../../util/reactIntl';
import { parseDateFromISO8601, stringifyDateToISO8601 } from '../../../util/dates';

import { FieldDateRangeController, FieldSelect } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import FilterPopupForSidebar from './FilterPopupForSidebar';
import css from './BookingDateRangeFilter.module.css';
import moment from 'moment';

const HOURS = Array(23).fill();
const getHours = key => {
  const updatedStartHours = [];
  HOURS.map((v, h) => {
    if (key == 'start') {
      if (h > 9) updatedStartHours.push(`${h}:00`, `${h}:30`);
      else updatedStartHours.push(`0${h}:00`, `0${h}:30`);
    } else if (key == 'end') {
      if (h > 9) updatedStartHours.push(`${h + 1}:00`, `${h + 1}:30`);
      else updatedStartHours.push(`0${h + 1}:00`, `0${h + 1}:30`);
    }
  });
  // End-of-day sentinel; Sharetribe stores this as 00:00 on the plan entry.
  if (key == 'end') {
    updatedStartHours.push('24:00');
  }
  return updatedStartHours;
};
const ALL_START_HOURS = getHours('start');
const ALL_END_HOURS = getHours('end');

const getDatesQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'dates';
};

// Parse query parameter, which should look like "2020-05-28,2020-05-31"
const parseValue = (date, time) => {
  const rawValuesFromParams = date ? date.split(',') : [];
  const rawValuesForTime = time ? time.split(',') : [];
  const [startDate, endDate] = rawValuesFromParams.map(v => parseDateFromISO8601(v));
  const [startTime, endTime] = rawValuesForTime.map(v => v.trim());
  // return value && startDate && endDate ? { dates: { startDate, endDate } } : { dates: null };
  return date && startDate && endDate
    ? { dates: { startDate, endDate }, startHours: startTime, endHours: endTime }
    : { dates: null, startHours: startTime, endHours: endTime };
};
// Format dateRange value for the query. It's given by FieldDateRangeInput:
// { dates: { startDate, endDate } }
const formatValue = (dateRange, queryParamName) => {
  const hasDates = dateRange && dateRange.dates;
  const { startDate, endDate } = hasDates ? dateRange.dates : {};
  const { startHours, endHours } = dateRange || {};
  const start = startDate ? stringifyDateToISO8601(startDate) : null;
  // const end = endDate ? stringifyDateToISO8601(endDate) : null;
  const end = start;
  const value = start && end ? `${start},${end}` : null;
  const timeValue = start && end ? `${startHours},${endHours}` : null;
  const newObj = { [queryParamName]: value, ['time']: timeValue };
  return newObj;
};

export class BookingDateRangeFilterComponent extends Component {
  constructor(props) {
    super(props);

    this.state = { isOpen: true, start: null };

    this.popupControllerRef = null;
    this.plainControllerRef = null;

    this.toggleIsOpen = this.toggleIsOpen.bind(this);
  }

  toggleIsOpen() {
    this.setState(prevState => ({ isOpen: !prevState.isOpen }));
  }

  render() {
    const {
      className,
      rootClassName,
      showAsPopup,
      isDesktop,
      initialValues,
      initialValuesForTime,
      id,
      contentPlacementOffset,
      onSubmit,
      queryParamNames,
      label,
      intl,
      minimumNights,
      ...rest
    } = this.props;

    const datesQueryParamName = getDatesQueryParamName(queryParamNames);
    const initialDates =
      initialValues && initialValues[datesQueryParamName]
        ? parseValue(initialValues[datesQueryParamName], initialValuesForTime['time'])
        : { dates: null };

    const isSelected = !!initialDates.dates;

    const startDate = isSelected ? initialDates.dates.startDate : null;
    const endDate = isSelected ? initialDates.dates.endDate : null;

    const format = {
      month: 'short',
      day: 'numeric',
    };

    const formattedStartDate = isSelected ? intl.formatDate(startDate, format) : null;
    const formattedEndDate = isSelected ? intl.formatDate(endDate, format) : null;

    const labelForPlain = isSelected
      ? intl.formatMessage(
          { id: 'BookingDateRangeFilter.labelSelectedPlain' },
          {
            dates: `${formattedStartDate} - ${formattedEndDate}`,
          }
        )
      : label
      ? label
      : intl.formatMessage({ id: 'BookingDateRangeFilter.labelPlain' });

    const labelForPopup = isSelected
      ? intl.formatMessage(
          { id: 'BookingDateRangeFilter.labelSelectedPopup' },
          {
            dates: `${formattedStartDate} - ${formattedEndDate}`,
          }
        )
      : label
      ? label
      : intl.formatMessage({ id: 'BookingDateRangeFilter.labelPopup' });

    const labelSelection = isSelected
      ? intl.formatMessage(
          { id: 'BookingDateRangeFilter.labelSelectedPopup' },
          {
            dates: `${formattedStartDate} - ${formattedEndDate}`,
          }
        )
      : null;

    const handleSubmit = values => {
      const { dates = {}, endHours = '', startHours = '' } = values || {};
      const hasDate = values && dates && dates?.startDate ? true : false;
      const hasStart = values && startHours ? true : false;
      const hasEnd = values && endHours ? true : false;
      const filterDate = hasDate ? dates?.startDate : moment().toDate();
      const filterStart = hasStart ? startHours : '00:00';
      const filterEnd = hasEnd && endHours > startHours ? endHours : '18:00';
      const newValues =
        values != null
          ? { dates: { startDate: filterDate }, endHours: filterEnd, startHours: filterStart }
          : null;
      onSubmit(formatValue(newValues, datesQueryParamName));
    };

    const onClearPopupMaybe =
      this.popupControllerRef && this.popupControllerRef.onReset
        ? { onClear: () => this.popupControllerRef.onReset(null, null) }
        : {};

    const onCancelPopupMaybe =
      this.popupControllerRef && this.popupControllerRef.onReset
        ? { onCancel: () => this.popupControllerRef.onReset(startDate, endDate) }
        : {};

    const onClearPlainMaybe =
      this.plainControllerRef && this.plainControllerRef.onReset
        ? { onClear: () => this.plainControllerRef.onReset(null, null) }
        : {};

    const startTimeLabel = intl.formatMessage({ id: 'bookingDateRangeFilter.startTimeText' });
    const endTimeLabel = intl.formatMessage({ id: 'bookingDateRangeFilter.endTimeText' });

    return showAsPopup ? (
      <FilterPopup
        className={className}
        rootClassName={rootClassName}
        popupClassName={css.popupSize}
        label={labelForPopup}
        isSelected={isSelected}
        id={`${id}.popup`}
        showAsPopup
        contentPlacementOffset={contentPlacementOffset}
        onSubmit={handleSubmit}
        {...onClearPopupMaybe}
        {...onCancelPopupMaybe}
        initialValues={initialDates}
        {...rest}
      >
        <FieldDateRangeController
          name="dates"
          minimumNights={minimumNights}
          controllerRef={node => {
            this.popupControllerRef = node;
          }}
          className={css.datePicker}
        />
        <div className={css.timeSelectHours}>
          <div>
            <FieldSelect
              id={'startHours'}
              className={css.selectTime}
              name={'startHours'}
              disabled={false}
              placeHolder={startTimeLabel}
            >
              <option disabled value={''}>
                {startTimeLabel}
              </option>
              {ALL_START_HOURS.map(hr => (
                <option key={hr} value={hr}>
                  {hr}
                </option>
              ))}
            </FieldSelect>
          </div>
          <div>
            <FieldSelect
              id={'endHours'}
              className={css.selectTime}
              name={'endHours'}
              disabled={false}
            >
              <option disabled value={''}>
                {endTimeLabel}
              </option>
              {ALL_END_HOURS.map(hr => (
                <option key={hr} value={hr}>
                  {hr}
                </option>
              ))}
            </FieldSelect>
          </div>
        </div>
      </FilterPopup>
    ) : isDesktop ? (
      <FilterPopupForSidebar
        className={className}
        rootClassName={rootClassName}
        popupClassName={css.popupSize}
        label={label}
        labelSelection={labelSelection}
        isSelected={isSelected}
        id={`${id}.popup`}
        showAsPopup
        contentPlacementOffset={contentPlacementOffset}
        onSubmit={handleSubmit}
        {...onClearPopupMaybe}
        {...onCancelPopupMaybe}
        initialValues={initialDates}
        {...rest}
      >
        <FieldDateRangeController
          name="dates"
          minimumNights={minimumNights}
          controllerRef={node => {
            this.popupControllerRef = node;
          }}
        />
        <div className={css.timeSelectHours}>
          <div>
            <FieldSelect
              id={'startHours'}
              className={css.selectTime}
              name={'startHours'}
              disabled={false}
              placeHolder={startTimeLabel}
            >
              <option disabled value={''}>
                {startTimeLabel}
              </option>
              {ALL_START_HOURS.map(hr => (
                <option key={hr} value={hr}>
                  {hr}
                </option>
              ))}
            </FieldSelect>
          </div>
          <div>
            <FieldSelect
              id={'endHours'}
              className={css.selectTime}
              name={'endHours'}
              disabled={false}
            >
              <option disabled value={''}>
                {endTimeLabel}
              </option>
              {ALL_END_HOURS.map(hr => (
                <option key={hr} value={hr}>
                  {hr}
                </option>
              ))}
            </FieldSelect>
          </div>
        </div>
      </FilterPopupForSidebar>
    ) : (
      <FilterPlain
        className={className}
        rootClassName={rootClassName}
        label={label}
        labelSelection={labelSelection}
        labelSelectionSeparator=":"
        isSelected={isSelected}
        id={`${id}.plain`}
        liveEdit
        contentPlacementOffset={contentPlacementOffset}
        onSubmit={handleSubmit}
        {...onClearPlainMaybe}
        initialValues={initialDates}
        {...rest}
      >
        <FieldDateRangeController
          name="dates"
          minimumNights={minimumNights}
          controllerRef={node => {
            this.plainControllerRef = node;
          }}
        />
        <div className={css.timeSelectHours}>
          <div>
            <FieldSelect
              id={'startHours'}
              className={css.selectTime}
              name={'startHours'}
              disabled={false}
              placeHolder={startTimeLabel}
            >
              <option disabled value={''}>
                {startTimeLabel}
              </option>
              {ALL_START_HOURS.map(hr => (
                <option key={hr} value={hr}>
                  {hr}
                </option>
              ))}
            </FieldSelect>
          </div>
          <div>
            <FieldSelect
              id={'endHours'}
              className={css.selectTime}
              name={'endHours'}
              disabled={false}
            >
              <option disabled value={''}>
                {endTimeLabel}
              </option>
              {ALL_END_HOURS.map(hr => (
                <option key={hr} value={hr}>
                  {hr}
                </option>
              ))}
            </FieldSelect>
          </div>
        </div>
      </FilterPlain>
    );
  }
}

BookingDateRangeFilterComponent.defaultProps = {
  rootClassName: null,
  className: null,
  showAsPopup: true,
  liveEdit: false,
  minimumNights: 0,
  initialValues: null,
  contentPlacementOffset: 0,
};

BookingDateRangeFilterComponent.propTypes = {
  rootClassName: string,
  className: string,
  id: string.isRequired,
  label: node,
  showAsPopup: bool,
  liveEdit: bool,
  queryParamNames: arrayOf(string).isRequired,
  onSubmit: func.isRequired,
  minimumNights: number,
  initialValues: object,
  contentPlacementOffset: number,

  // form injectIntl
  intl: intlShape.isRequired,
};

const BookingDateRangeFilter = injectIntl(BookingDateRangeFilterComponent);

export default BookingDateRangeFilter;
